






import { Component, Prop, Vue, Watch } from "vue-property-decorator";
// types
import { FolderEntites } from "../../../services/folders/types";
import { MediaType, Status, VscFile } from "@/services/files/types";
import i18n from "@/i18n";
// service methods
import { getApplicationSettings } from "@/services/applicationSettings/applicationSettingsService";
import { getRootFolder } from "@/services/folders/folderService";
import { postFile } from "@/services/files/fileService";
// components
import FileAlertModal from "@/components/files/modals/FileAlertModal.vue";
import { Guid } from "guid-typescript";
import { ApplicationSettings } from "@/services/applicationSettings/types";

    @Component({
        components: {
            FileAlertModal
        }
    })
export default class DirectFileUpload extends Vue {
        @Prop() folder!: FolderEntites;
        @Prop() promptFileUpload!: boolean;
        // local state
        vscFile = {} as VscFile;
        tmpFile = {} as File;
        directFolderId = "";
        allowedExtensions = "";
        applicationSettings = {} as ApplicationSettings;
        localFileTypes = [] as MediaType[];

        // lifecycle hooks
        mounted() {
            this.fetchData();
        }

        // watchers
        @Watch("vscFile", { deep: true }) vscFileChanged() {
            this.$emit("fileObjectChanged", this.vscFile);
        }

        // methods
        fetchData() {
            getApplicationSettings({ keys: ["MaxUploadFileSize", "AllowedImageExtensions", "AllowedVideoExtensions", "OtherAllowedExtensions"] }).then(response => {
                this.applicationSettings = response;
            });
            this.updateAllowedExtensions();
            getRootFolder(this.folder).then(response => {
                this.directFolderId = response.id;
            });
        }

        updateAllowedExtensions() {
            let allowedExtension = "";
            this.localFileTypes.forEach(type => {
                switch (type) {
                case MediaType.Image:
                    allowedExtension += this.applicationSettings.allowedImageExtensions;
                    break;
                case MediaType.Video:
                    allowedExtension += this.applicationSettings.allowedVideoExtensions;
                    break;
                case MediaType.Document:
                    allowedExtension += this.applicationSettings.otherAllowedExtensions;
                    break;
                }
            });
            // if there is no media type filtering, allow all available extensions
            if (allowedExtension === "") {
                allowedExtension += this.applicationSettings.allowedImageExtensions + this.applicationSettings.allowedVideoExtensions + this.applicationSettings.otherAllowedExtensions;
            }
            this.allowedExtensions = allowedExtension;
        }

        // this method opens file upload control
        openFileUpload(mediaTypes: MediaType[]): void {
            // update AllowedExtension before validation
            this.localFileTypes = mediaTypes;
            this.updateAllowedExtensions();
            (this.$refs.fileInput as any).click();
        }

        async onFileUploadChange(event: any) {
            this.tmpFile = event.target.files[0];
            if (!this.validateFile()) {
                return;
            }

            this.vscFile = {
                id: "",
                name: this.tmpFile.name,
                isNew: true,
                folderId: this.directFolderId
            } as VscFile;
            this.startUpload();
            event.target.value = "";
        }

        // this method is used if the user wants to inject file without using the file control
        injectFile(file: File, mediaTypes: MediaType[]) {
            // update AllowedExtension before validation
            this.localFileTypes = mediaTypes;
            this.updateAllowedExtensions();

            this.tmpFile = file;
            if (!this.validateFile()) {
                return;
            }
            this.vscFile = {
                id: "",
                name: this.tmpFile.name,
                isNew: true,
                folderId: this.directFolderId
            } as VscFile;
            this.startUpload();
        }

        startUpload() {
            if (!window.navigator.onLine) {
                this.$emit("fileUploadFail");
                return;
            }
            const componentScope = this;
            const config = {
                onUploadProgress: function (event: ProgressEvent) {
                    if (event.lengthComputable) {
                        if (componentScope.vscFile) {
                            Vue.set(componentScope.vscFile, "percentage", event.loaded / event.total * 90);
                        }
                    }
                }
            };

            const formData = new FormData();

            this.$emit("startUploading", { fileId: this.vscFile.id, name: this.vscFile.name });

            // Prepend a guid before the name to prevent our backend from just replacing the same image
            formData.append("file", this.tmpFile, Guid.create() + "_" + this.tmpFile.name);
            formData.append("folderId", this.vscFile.folderId);
            this.vscFile.status = Status.uploading;

            this.toggleHeaderButtons(false);

            let isUploading = true;
            const checkConnection = setInterval(() => {
                if (!window.navigator.onLine && isUploading) {
                    if (this.vscFile) {
                        this.vscFile.status = Status.error;
                        this.vscFile.percentage = 0;
                        this.$emit("fileUploadFail");
                    }
                    isUploading = false;
                    clearInterval(checkConnection);
                    this.toggleHeaderButtons(true);
                }
            }, 500); // check every 500ms

            postFile(formData, config).then(response => {
                if (this.vscFile) {
                    this.vscFile.id = response.id;
                    this.vscFile.name = response.name;
                    this.vscFile.status = Status.done;
                    this.vscFile.percentage = 100;
                    this.vscFile.mediaType = response.mediaType;

                    this.$emit("fileUploaded", { fileId: this.vscFile.id, name: this.vscFile.name, originalFileName: this.tmpFile.name, mediaType: this.vscFile.mediaType });
                    this.toggleHeaderButtons(true);
                }
            }).catch(() => {
                if (this.vscFile) {
                    this.vscFile.status = Status.error;
                    this.vscFile.percentage = 0;
                    this.$emit("fileUploadFail");
                }
                this.toggleHeaderButtons(true);
            });
        }

        validateFile(): boolean {
            // check size
            if (this.applicationSettings.maxUploadFileSize === "" || (this.tmpFile.size / 1048576) > parseInt(this.applicationSettings.maxUploadFileSize, 10)) {
                (this.$refs.fileAlertModal as any).open(i18n.t("file.fileSizeExceeds"));
                return false;
            }

            const fileExtension = this.tmpFile.name.split(".").pop()!;
            if (this.allowedExtensions === "" || !this.allowedExtensions.includes(fileExtension.toLowerCase())) {
                (this.$refs.fileAlertModal as any).open(i18n.t("file.extensionNotAllowed"));
                return false;
            }

            return true;
        }

        retryUpload() {
            this.vscFile.status = Status.uploading;
            this.vscFile.percentage = 0;
            this.startUpload();
        }

        toggleHeaderButtons(state: boolean) {
            this.$emit("toggleHeaderButtons", state);
        }

        removeFile() {
            this.tmpFile = {} as File;
            this.vscFile = {} as VscFile;
        }
}
